import { useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { Autocomplete, TextField } from '@mui/material';
import { GET_LICENSES } from '../licenses/overview/License.graphql';
import { GET_LICENSE } from '../licenses/details/LicenseInfo.graphql';
import { useDebouncedSearch } from './useDebouncedSearch';

export interface PickedLicense {
  id: number;
  companyName: string | null;
  label: string;
}

interface Props {
  value: PickedLicense | null;
  onChange(value: PickedLicense | null): void;
  unavailable?: { ids: ReadonlySet<number>; reason: string };
  disabled?: boolean;
  autoFocus?: boolean;
}

const isNumeric = (term: string) => /^\d+$/.test(term);

const toOption = (l: { id: number; companyName?: string | null; orgNumber?: string | null }): PickedLicense => ({
  id: l.id,
  companyName: l.companyName ?? null,
  label: `${l.companyName || 'Uten navn'} (${l.id})${l.orgNumber ? ` · org.nr ${l.orgNumber}` : ''}`,
});

export default function LicensePicker({ value, onChange, unavailable, disabled, autoFocus }: Props) {
  const [term, setTerm] = useState('');
  const [searchLicenses, search] = useLazyQuery<any, any>(GET_LICENSES, { fetchPolicy: 'no-cache' });
  const [getLicenseById, byId] = useLazyQuery<any, any>(GET_LICENSE, { fetchPolicy: 'no-cache' });

  // onlyInformed MUST be false: AllLicenses treats a missing value as true.
  const control = useDebouncedSearch((t) =>
    Promise.all([
      searchLicenses({ variables: { page: 0, pageSize: 20, searchTerm: t, onlyActive: true, onlyInformed: false } }),
      isNumeric(t) ? getLicenseById({ variables: { id: Number(t) } }) : Promise.resolve(),
    ]),
  );

  // The lazy queries keep their last data; show a by-id hit only while the term is still that id.
  const byIdHit = byId.data?.license?.bksLicense;
  const showByIdHit = byIdHit && isNumeric(term.trim()) && Number(term.trim()) === byIdHit.id;
  const options = [...(showByIdHit ? [toOption(byIdHit)] : []), ...(search.data?.licenses?.licenses ?? []).map(toOption)].filter(
    (o, i, all) => all.findIndex((x) => x.id === o.id) === i,
  );
  const isUnavailable = (o: PickedLicense) => !!unavailable?.ids.has(o.id);

  return (
    <Autocomplete
      options={options}
      value={value}
      disabled={disabled}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v, reason) => {
        if (reason !== 'input') return;
        setTerm(v);
        control.onInput(v);
      }}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionDisabled={isUnavailable}
      getOptionLabel={(o) => (isUnavailable(o) ? `${o.label} · ${unavailable!.reason}` : o.label)}
      loading={control.pending || search.loading}
      loadingText="Søker …"
      filterOptions={(x) => x}
      noOptionsText="Søk på firmanavn, org.nr eller kundenummer"
      renderInput={(params) => <TextField {...params} label="Kunde" placeholder="Søk etter kunde …" required autoFocus={autoFocus} />}
    />
  );
}
